/**
 * <ActivityChrono> — le chrono d'une activité, dans son en-tête (molecule).
 *
 * L'ancien `TimerContainer` : le temps qui RESTE, en minutes et secondes, qui passe sous zéro
 * quand l'élève dépasse la durée prévue. Trois états, lus sur les props :
 *  - en cours : le chronomètre, le temps restant ;
 *  - bientôt fini ou dépassé (95 % de la durée prévue, le seuil d'aujourd'hui) : la tinte
 *    d'alerte, qui dit « regarde l'heure » sans être une erreur ;
 *  - en pause : le glyphe de pause, l'encre secondaire. C'est le tuteur qui met en pause, en
 *    séance (mode Concentré, quiz en cours) : l'élève n'a pas de bouton, comme aujourd'hui.
 *
 * Contrôlé : le temps arrive en props. Quand il court (`runningSince`, l'instant où
 * `remainingSeconds` valait), le chrono porte son propre décompte : c'est lui qui se redessine chaque
 * seconde, pas la page ni le live qui la pose. Sans `runningSince`, il montre `remainingSeconds` tel
 * quel. Il ne presse pas ; son nom accessible dit ce que l'infobulle d'aujourd'hui disait (« Temps
 * restant », « Temps dépassé »).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatCountdown } from "../../../ports/primary/ui/formatters/dateFormatter"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { useSecondClock } from "../../hooks/useSecondClock"

/** La part de la durée prévue à partir de laquelle le chrono passe à l'alerte — celle de l'ancien code. */
const CHRONO_WARNING_FROM = 0.95

const TEXTS = {
    remaining: (time: string) => `Temps restant ${time}`,
    over: (time: string) => `Temps dépassé ${time}`,
    paused: "Chrono en pause",
}

export type ActivityChronoProps = {
    /** Le temps qui reste, en secondes. Négatif : la durée prévue est dépassée. */
    remainingSeconds: number
    /** La durée prévue, en secondes : elle place le seuil d'alerte. */
    estimatedSeconds: number
    /** Le tuteur a mis l'activité en pause. Défaut `false`. */
    paused?: boolean
    /**
     * Le chrono court : `Date.now()` de l'instant où `remainingSeconds` valait. Il décompte alors seul,
     * à la seconde. Absent ou `null` : il montre `remainingSeconds` (défaut).
     */
    runningSince?: number | null
    testID?: string
}

/** Le temps qui reste, décompté depuis `runningSince` quand il court. */
function useCountdown(remainingSeconds: number, runningSince: number | null | undefined, paused: boolean): number {
    const since = runningSince != null && !paused ? runningSince : null
    const now = useSecondClock(since)
    if (since == null) return remainingSeconds
    return remainingSeconds - Math.max(0, Math.floor((now - since) / 1000))
}

export function ActivityChrono(props: ActivityChronoProps) {
    const { estimatedSeconds, paused = false, runningSince, testID } = props
    const remainingSeconds = useCountdown(props.remainingSeconds, runningSince, paused)

    const used = estimatedSeconds > 0 ? (estimatedSeconds - remainingSeconds) / estimatedSeconds : 0
    const warning = !paused && used >= CHRONO_WARNING_FROM
    const time = formatCountdown(remainingSeconds)
    const label = paused ? TEXTS.paused : remainingSeconds < 0 ? TEXTS.over(time) : TEXTS.remaining(time)
    const ink = paused ? "text-muted" : warning ? "warning-strong" : "text-primary"

    return (
        <View style={styles.chip(warning)} accessible accessibilityLabel={label} testID={testID}>
            <Icon
                name={paused ? "pause" : "stopwatch"}
                size={16}
                color={paused ? "text-muted" : warning ? ink : "primary"}
            />
            <Text size="text-md" weight="semibold" color={ink} style={styles.time}>
                {time}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    chip: (warning: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["1"],
        height: 32,
        paddingHorizontal: theme.spacing["3"],
        borderRadius: theme.radius.full,
        backgroundColor: warning ? theme.colors.warningLight : theme.colors["surface-subtle"],
    }),
    /** Les chiffres ne dansent pas d'une seconde à l'autre. */
    time: { fontVariant: ["tabular-nums" as const] },
}))
